import { LegacyForm } from "PFApp/components/form/legacy_form";
import PositionPeriodPicker from "PFApp/profiles/edit/fields/position_period_picker";
import { Select } from "PFComponents/select";
import SidePanel from "PFComponents/side_panel/side_panel";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import api from "PFCore/api";
import { utc } from "PFCore/helpers/date";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Activity, Profile } from "PFCore/types";
import { Option } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./edit_internal_experience_form.module.scss";
import { ExperienceCustomTypeField } from "./experience_custom_type_field/experience_custom_type_field";
import { useExperienceCustomFields } from "./use_experience_custom_fields";
import { useExperienceFormSubmission } from "./use_experience_form_submission";

const ENGAGEMENT_HOURS_MAX_VALUE = 10 * 365 * 24; // 10 years in hours
const ENGAGEMENT_HOURS_MIN_VALUE = 0;

type EditInternalExperienceFormProps = {
  experience?: Activity;
  adminPage: boolean;
  profile: Profile;
  handleSuccess: (experience: Activity) => void;
  handleRemove: () => void;
  onClose: () => void;
};

const EditInternalExperienceForm = ({
  experience,
  adminPage,
  profile,
  handleSuccess,
  handleRemove,
  onClose
}: EditInternalExperienceFormProps) => {
  const { formatDatabaseDateTime, formatISODate } = useDateFormatter();

  const dateRange = experience?.metadata.date_range || {};

  const [positionId, setPositionId] = useState(experience?.metadata.position_id);
  const [startDate, setStartDate] = useState(
    dateRange.start_date ? formatISODate(utc(dateRange.start_date)) : null
  );
  const [endDate, setEndDate] = useState(dateRange.end_date ? formatISODate(utc(dateRange.end_date)) : null);
  const [current, setCurrent] = useState(dateRange.current || false);
  const [location, setLocation] = useState(experience?.metadata.location || "");
  const [engagementHours, setEngagementHours] = useState(experience?.metadata.engagement_hours);
  const [description, setDescription] = useState(experience?.description || "");
  const [name, setName] = useState(experience?.name || "");

  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("profiles", { keyPrefix: "common.experience" });

  const isNew = !experience?.id;
  const engagementHoursMaxValueWarningTip =
    engagementHours >= ENGAGEMENT_HOURS_MAX_VALUE
      ? t("internalExperience.edit.hours.maxValueWarningTip", { value: ENGAGEMENT_HOURS_MAX_VALUE })
      : undefined;

  const {
    customFields,
    getCustomFieldValues,
    updateCustomField,
    brandsCustomType,
    sectorsCustomType,
    skillsCustomType
  } = useExperienceCustomFields({
    initialCustomFields: experience?.custom_fields || [],
    adminPage,
    profile,
    currentProfile
  });

  const { errors, isDirty, handleSubmit, handleRemoveCallback, handleSuccessCallback, handleError } =
    useExperienceFormSubmission({
      experience,
      isNew,
      profile,
      formatDatabaseDateTime,
      handleSuccess,
      handleRemove,
      values: {
        positionId,
        startDate,
        endDate,
        current,
        location,
        engagementHours,
        description,
        name,
        customFields
      }
    });

  return (
    <SidePanel
      show
      fullHeight
      isOnTop
      size="medium"
      title={isNew ? t("position.newExperience") : t("position.editPosition")}
      onClose={onClose}
    >
      <LegacyForm
        blockNavigationOnChange
        growlErrors
        isDirty={isDirty}
        isNew={isNew}
        onError={handleError}
        onRemove={handleRemoveCallback}
        onSubmit={handleSubmit}
        onSuccess={handleSuccessCallback}
        qaIdPrefix="profile-edit-past-experience"
      >
        <div className={css.form}>
          <Select
            label={t("internalExperience.edit.position.label")}
            tip={t("internalExperience.edit.position.tip")}
            value={positionId}
            fetchOptions={({ ids }) =>
              api({
                url: "options/positions",
                params: {
                  profileId: profile?.id,
                  value: ids
                }
              }).then((response) => ({
                ...response,
                entries: response.entries.map((position) => ({
                  id: position.id,
                  value: position.text,
                  original: position
                }))
              }))
            }
            error={errors["metadata[positionId]"]}
            onChange={(option: Option<{ id: string; value: string }>) => {
              if (option) {
                setPositionId(option.id);
              }
            }}
            disabled={!!experience?.metadata.position_id}
          />
          <InputFieldSet
            label={t("internalExperience.edit.roleTitle.label")}
            tip={t("internalExperience.edit.roleTitle.tip")}
            value={name}
            inputType="text"
            qaId="name"
            error={errors["name"]}
            onChange={(value) => setName(value)}
          />
          {brandsCustomType && (
            <ExperienceCustomTypeField
              customType={brandsCustomType}
              tip={t("internalExperience.edit.client")}
              values={getCustomFieldValues("brands")}
              errors={errors["customFields[brands]"]}
              adminPage={adminPage}
              isMulti={false}
              profile={profile}
              onChange={(values) => updateCustomField(brandsCustomType, values)}
            />
          )}
          {sectorsCustomType && (
            <ExperienceCustomTypeField
              customType={sectorsCustomType}
              tip={t("internalExperience.edit.industries")}
              values={getCustomFieldValues("sectors")}
              errors={errors["customFields[sectors]"]}
              adminPage={adminPage}
              profile={profile}
              onChange={(values) => updateCustomField(sectorsCustomType, values)}
            />
          )}
          {skillsCustomType && (
            <ExperienceCustomTypeField
              customType={skillsCustomType}
              tip={t("internalExperience.edit.skills")}
              values={getCustomFieldValues("skills")}
              errors={errors["customFields[skills]"]}
              adminPage={adminPage}
              profile={profile}
              onChange={(values) => updateCustomField(skillsCustomType, values)}
            />
          )}
          <InputFieldSet
            label={t("internalExperience.edit.location.label")}
            tip={t("internalExperience.edit.location.tip")}
            value={location}
            inputType="text"
            qaId="metadata[location]"
            error={errors["metadata[location]"]}
            onChange={(value) => setLocation(value)}
          />
          <InputFieldSet
            label={t("internalExperience.edit.description.label")}
            tip={t("internalExperience.edit.description.tip")}
            value={description}
            inputType="textarea"
            qaId="description"
            error={errors["description"]}
            onChange={(value) => setDescription(value)}
          />
          <InputFieldSet
            label={t("internalExperience.edit.hours.label")}
            tip={t("internalExperience.edit.hours.tip")}
            value={isNaN(engagementHours) ? undefined : `${engagementHours}`}
            max={ENGAGEMENT_HOURS_MAX_VALUE}
            min={ENGAGEMENT_HOURS_MIN_VALUE}
            inputType="number"
            qaId="metadata[engagementHours]"
            error={errors["metadata[engagementHours]"]}
            warning={engagementHoursMaxValueWarningTip}
            onKeyDown={(event) => {
              if (!event.key.match(/^[0-9]+|Backspace$/)) {
                event.preventDefault();
              }
            }}
            onChange={(value) =>
              setEngagementHours(
                isNaN(parseInt(value)) ? undefined : Math.min(parseInt(value), ENGAGEMENT_HOURS_MAX_VALUE)
              )
            }
          />
          <PositionPeriodPicker
            error={
              errors["metadata[dateRange][startDate]"] ||
              errors["metadata[dateRange][endDate]"] ||
              errors["metadata[dateRange]"]
            }
            showLabel
            currentPosition={current}
            startDate={startDate ? formatISODate(startDate) : null}
            endDate={endDate ? formatISODate(endDate) : null}
            handleToggleClick={(current) => setCurrent(current)}
            handleChange={(startDate, endDate) => {
              setStartDate(startDate);

              if (endDate && !current) {
                setEndDate(endDate);
              } else {
                setEndDate(null);
              }
            }}
          />
        </div>
      </LegacyForm>
    </SidePanel>
  );
};

export default EditInternalExperienceForm;
